import React, { useEffect, useState } from "react";
import { X, Plus } from "lucide-react";
import axiosInstanceAuth from "../../apiInstances/axiosInstanceAuth";
import { useCollections } from "../../Hooks/useCollections/useCollections";
import { useGetAllListings } from "../../Hooks/useGetAllListings/useGetAllListings";
import { useFavoriteEvent } from "../../context/FavoriteContext";
import logo from "../../assets/home/home.svg";

// Props:
// - open: boolean
// - onClose: () => void
// - collections: Array<{ id: string; name: string; thumbnail?: string }>
// - initialSelectedIds: string[]
// - onCreateNewCollection: () => void
// - showSavedRow: boolean

export function CollectionPickerModal({
  open,
  onClose,
  propertyId,
  initialSelectedIds = [],
  onCreateNewCollection,
}) {
  const [selected, setSelected] = useState(new Set(initialSelectedIds));
  const { collections, refetch } = useCollections();
  const { triggerFavoriteEvent } = useFavoriteEvent();
  useEffect(() => {
    setSelected(new Set(initialSelectedIds));
  }, [open]);

  if (!open) return null;

  const toggleId = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleDone = async () => {
    try {
      const selectedIds = Array.from(selected);
      await axiosInstanceAuth.post("/collection/addProperty", {
        collectionIds: selectedIds,
        propertyId,
      });
      refetch();
      triggerFavoriteEvent(propertyId, "like");
    } catch (e) {
      // optional: toast error here if you want
    } finally {
      onClose && onClose();
    }
  };

  const handleCreateNew = () => {
    if (onClose) onClose();
    if (onCreateNewCollection) onCreateNewCollection();
  };

  const isChecked = (id) => selected.has(id);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="relative w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-[#000929]">
            Your Collections
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="max-h-[360px] overflow-y-auto px-5 py-2 space-y-2">
          {(collections || []).map((col) => (
            <button
              key={col.id}
              type="button"
              onClick={() => toggleId(col.id)}
              className="w-full flex items-center justify-between gap-3 rounded-xl px-3 py-2 hover:bg-gray-50 border border-transparent"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-md bg-gray-100 overflow-hidden flex items-center justify-center">
                  {col.thumbnail ? (
                    <img
                      src={col.thumbnail}
                      alt={col.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <img src={logo} alt="logo" className="w-8 h-8" />
                  )}
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-[#000929]">
                    {col.name}
                  </p>
                </div>
              </div>
              <input
                type="checkbox"
                readOnly
                checked={isChecked(col.id)}
                className="w-4 h-4 rounded border-gray-300 text-[#E30613] focus:ring-[#E30613]"
              />
            </button>
          ))}

          <button
            type="button"
            onClick={handleCreateNew}
            className="w-full flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 hover:bg-gray-50 border border-dashed border-gray-300 mt-2"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md border border-gray-300 flex items-center justify-center text-gray-500">
                <Plus className="w-4 h-4" />
              </div>
              <p className="text-sm font-medium text-[#000929]">
                Create new Collection
              </p>
            </div>
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-3 rounded-full border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDone}
            className="px-5 py-3 rounded-full bg-[#097FE6] text-white text-sm font-semibold hover:bg-[#097FE6]"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
